
import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { CloseSvg, ImportSvg, InfoSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { FC, Fragment, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { StepperProps } from '../../../utils/stepper-types'
import useForm from '../../../hooks/useForm'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { DEFAULT_ORBIT_COLOR } from '../../../../../../utils/constants'
import useStaticData from '../../../hooks/useStaticData'
import { getInvalidRangeErrorMessage, handleJsonFileUpload, showToastMessage, validateNumberWithoutDecimal } from '../../../../../../utils/commonFunctions'
import useModal from '../../../hooks/useModal'
import useUserData from '../../../hooks/useUserData'
import Select from '../../../../../CommonComponents/select/Select'
import DatetimeInput from '../../date-time-input/DatetimeInput'
import useScenarioResponse from '../../../hooks/useScenarioResponse'
import { validateSameScheduleDetails } from '../../../utils/validation'
import { resetValidatePresimData } from '../../../utils/helperFunction'
import { SCENARIO_OPERATION_TYPES } from '../../../../constants'
import useViewer from '../../../hooks/useViewer'
import useBaseApi from '../../../hooks/useBaseApi'
import { CircularProgress } from '@mui/material'
import useOrbitAnimationDetails from '../../../hooks/useOrbitAnimationDetails'
import dayjs from 'dayjs'

const ScenarioSchedule: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {

    const { setModals } = useModal()
    const { userData } = useUserData()
    const { updateGroundPackets } = useViewer()
    const { formData, setFormData } = useForm()
    const { getTaskPlansByGroupId } = useBaseApi()
    const { scenarioResponse } = useScenarioResponse()
    const { stopPresimAnimation } = useOrbitAnimationDetails()
    const { allTaskPlans, setScenarioBaseData } = useStaticData()
    const [loading, setLoading] = useState<boolean | string>(false)

    const selectedSatellites = useMemo(() => formData.satellites, [formData.satellites])
    const scenarioStartTime = useMemo(() => formData.details.scenarioStartTime, [formData.details.scenarioStartTime])
    const scenarioDuration = useMemo(() => formData.details.scenarioDuration, [formData.details.scenarioDuration])
    const scenarioOperationType = useMemo(() => formData.details.scenarioOperation, [formData.details.scenarioOperation])
    const scenarioDeploymentType = useMemo(() => formData.details.scenarioDeploymentType, [formData.details.scenarioDeploymentType])
    const selectedTaskPlans = useMemo(() => formData.schedule.taskplans || [], [formData.schedule.taskplans])
    const scheduleType = useMemo(() => formData.schedule.scheduleType, [formData.schedule.scheduleType])
    const fidelity = useMemo(() => formData.assets.presim.fidelityType, [formData.assets.presim.fidelityType])
    const simulationStartTime = useMemo(() => formData.schedule.simulationStartTime, [formData.schedule.simulationStartTime])
    const simulationDuration = useMemo(() => formData.schedule.simulationDuration, [formData.schedule.simulationDuration])
    const showFidelityConfig = true //disabling for now
    const showConopsDetails = useMemo(() => scenarioOperationType === SCENARIO_OPERATION_TYPES.CONOPS, [scenarioOperationType])

    const validateSimulationTime = (date: number, duration: number | string, scenarioStartTime: number, scenarioDuration: number) => {
        // check the date is valid or not
        if (!date) {
            showToastMessage('info', "Please enter valid simulation start time")
            return false
        } else if (date < scenarioStartTime || (date + (duration as number) * 60) > (scenarioStartTime + (scenarioDuration as number) * 60)) {
            showToastMessage('info', (date < scenarioStartTime) ? "Simulation start time must be greater than scenario start time" : "The simulation window must be limited to a 24-hour period from the scenario's start time")
            return false
        } else {
            return true
        }
    }

    const validateDuration = (date: number, duration: number | string, scenarioStartTime: number, scenarioDuration: number) => {
        const scenarioEndTime = scenarioStartTime + (scenarioDuration * 60)
        const remainingDuration = Math.floor((scenarioEndTime - date) / 60)
        if (duration as number > remainingDuration) {
            showToastMessage('info', getInvalidRangeErrorMessage('Duration', 10, remainingDuration, 'minutes'))
            return false
        }
        return true
    }

    const resetSelectionModal = () => {
        setModals((prev) => ({
            ...prev,
            configured: null
        }))
        return showToastMessage('info', 'No Task Plan present for this Satellite Group')
    }

    const getTaskPlans = async (groupId: string) => {
        setLoading(groupId)
        try {
            const taskPlans = await getTaskPlansByGroupId(groupId)
            setScenarioBaseData((prev) => {
                return {
                    ...prev,
                    allTaskPlans: {
                        ...prev.allTaskPlans,
                        [groupId]: taskPlans || []
                    }
                }
            })
            if (!taskPlans.length) {
                return resetSelectionModal()
            }
            setModals((prev) => ({
                ...prev,
                configured: {
                    open: true,
                    name: 'taskplan',
                    data: {
                        allTaskPlans: taskPlans,
                        selectedTaskPlan: null
                    }
                }
            }))
        } catch (error) {
            return resetSelectionModal()
        } finally {
            setLoading(false)
        }
    }

    const openTaskPlanModal = async (groupId: string) => {
        if (!allTaskPlans[groupId]) {
            await getTaskPlans(groupId)
        } else if (allTaskPlans[groupId]?.length === 0) {
            return resetSelectionModal()
        } else {
            setModals((prev) => ({
                ...prev,
                configured: {
                    open: true,
                    name: 'taskplan',
                    data: {
                        allTaskPlans: allTaskPlans[groupId],
                        selectedTaskPlan: null
                    }
                }
            }))
        }
    }

    const onChangeFormData = (key: string, value: any) => {
        setFormData((prev) => {
            const schedule = {
                ...prev.schedule,
                [key]: value
            }
            const status = prev.assets.schedule.data ? validateSameScheduleDetails(schedule, scenarioResponse!) ? 'completed' : 'invalidated' : prev.assets.schedule.status
            const assets = {
                ...prev.assets,
                schedule: {
                    ...prev.assets.schedule,
                    status: status
                }
            }
            const data = {
                ...prev,
                schedule: schedule,
                assets: assets
            }
            if (key === 'taskplans') {
                updateGroundPackets(data)
            }
            return data
        })
    }

    const removeTaskPlan = (groupId: string) => {
        onChangeFormData('taskplans', formData.schedule.taskplans?.filter((taskPlan) => taskPlan?.satelliteGroupId !== groupId))
    }


    const onChangePresimDetails = (value: string) => {
        setFormData((prev) => {
            return {
                ...prev,
                assets: {
                    ...prev.assets,
                    presim: {
                        fidelityType: value,
                        data: resetValidatePresimData(prev?.assets?.presim?.data)
                    }
                }
            };
        });
        stopPresimAnimation()
    }

    const debouncedSimulationTime = useCallback(
        debounce((value: number, duration: number, start: number, scenarioDur: number, currentSimStart: number) => {
            const validate = validateSimulationTime(value, duration, start, scenarioDur)
            const time = currentSimStart === start ? start + 1 : start
            onChangeSimulationDetails('simulationStartTime', validate ? value : time)
        }, 1000),
        []
    )

    const onChangeSimulationTime = (value: number) => {
        if (!value || !dayjs(value).isValid()) return
        debouncedSimulationTime(value, simulationDuration, scenarioStartTime, scenarioDuration, simulationStartTime)
    }

    const onChangeDuration = (value: string) => {
        const duration = value ? Number(value) : ''
        const valid = validateDuration(simulationStartTime, duration, scenarioStartTime, scenarioDuration)
        if (!valid) return
        onChangeSimulationDetails('simulationDuration', duration)
    }

    const onChangeSimulationDetails = (key: 'simulationStartTime' | 'simulationDuration', value: number | string) => {
        onChangeFormData(key, !value ? '' : Number(value))
    }

    const onFileUpload = async () => {
        const response = await handleJsonFileUpload()
        if (response.status === 'success') {
            setFormData((prev) => ({
                ...prev,
                schedule: {
                    ...prev?.schedule,
                    scheduleType: 'import',
                    importedSchedule: response.result,
                },
                assets: {
                    ...prev?.assets,
                    schedule: {
                        ...prev?.assets?.schedule,
                        status: prev?.assets?.schedule?.data ? 'invalidated' : prev?.assets?.schedule?.status
                    }
                }
            }))
            showToastMessage('success', response.message)
        } else {
            showToastMessage('error', response.message)
        }
    }

    const onFileRemove = () => {
        setFormData((prev) => ({
            ...prev,
            schedule: {
                ...prev?.schedule,
                scheduleType: 'custom',
                importedSchedule: null,
            },
            assets: {
                ...prev?.assets,
                schedule: {
                    ...prev?.assets?.schedule,
                    status: prev?.assets?.schedule?.data ? 'completed' : prev?.assets?.schedule?.status
                }
            }
        }))
    }

    useEffect(() => {
        return () => {
            debouncedSimulationTime.cancel()
        }
    }, [debouncedSimulationTime])

    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                {showConopsDetails &&
                    <button className={`schedule__import__button ${stepCompleted && 'align__left'}`}
                        onClick={onFileUpload}
                        disabled={disabled || scheduleType === 'import'}
                    >
                        <ImportSvg />
                        <span className='fs-14'>Import</span>
                    </button>
                }
                {scheduleType === 'import' &&
                    <div className='imported__schedule__container'>
                        <div className='imported__schedule__details__container'>
                            <div className='imported__schedule__icon'><ImportSvg /></div>
                            <div className='imported__schedule__name'>
                                <CustomToolTip title={formData.schedule?.importedSchedule?.name || ''}>
                                    {formData.schedule?.importedSchedule?.name || ''}
                                </CustomToolTip>
                            </div>
                        </div>
                        <CustomToolTip title='Remove'>
                            <button className='imported__schedule__close__button' onClick={onFileRemove}><CloseSvg /></button>
                        </CustomToolTip>
                    </div>
                }
                {scheduleType === 'custom' &&
                    <Fragment>
                        {showConopsDetails &&
                            <div className='form__input__container vertical__align'>
                                <span className='form__input__label'>Task Plan</span>
                                {selectedSatellites.map((satellite) => {
                                    const satelliteName = scenarioDeploymentType === 'HARDWARE_IN_LOOP' ? satellite?.satelliteName : satellite?.groupName
                                    const taskPlanName = selectedTaskPlans.find((taskPlan) => taskPlan?.satelliteGroupId === satellite?.groupId)?.taskPlanName
                                    const buttonText = loading === satellite?.groupId ? 'Fetching' : taskPlanName || 'No Task Plans'
                                    const showLoading = loading === satellite?.groupId
                                    return (
                                        <div key={satellite.groupId} className='form__field__card'>
                                            <div className='Form__card__details__container'>
                                                <div className='form__field__card__color__box' style={{ background: (satellite?.color !== '' && !satellite?.color.includes('#')) ? `rgba(${JSON.parse(satellite?.color).join(",")})` : `rgba(${DEFAULT_ORBIT_COLOR.join(",")})` }} />
                                                <div className='form__field__card__text'>
                                                    <CustomToolTip title={satelliteName}>
                                                        {satelliteName}
                                                    </CustomToolTip>
                                                </div>
                                            </div>
                                            <button
                                                disabled={loading ? true : false}
                                                className={`form__field__card__button modal__trigger__button ${taskPlanName && 'active'}`}
                                                onClick={() => {
                                                    if (taskPlanName) return
                                                    openTaskPlanModal(satellite?.groupId)
                                                }}
                                            >
                                                <div className='card__button__text'>
                                                    <CustomToolTip title={taskPlanName || ''} className='w-100'>
                                                        <div className='d-flex align-items-center gap-2 w-100'>
                                                            <span className='text-truncate w-100'>
                                                                {buttonText}
                                                            </span>
                                                            {showLoading && <CircularProgress color='inherit' size={14} />}
                                                        </div>
                                                    </CustomToolTip>
                                                </div>
                                                {taskPlanName &&
                                                    <CustomToolTip title='Remove'>
                                                        <button className='card__button__icon'
                                                            onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                                                event.stopPropagation()
                                                                removeTaskPlan(satellite?.groupId)
                                                            }}
                                                        >
                                                            <CloseSvg />
                                                        </button>
                                                    </CustomToolTip>
                                                }
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        }
                        {showFidelityConfig &&
                            <div className='form__input__container presim__fidelity__section opacity-50'>
                                <span className='form__input__label'>
                                    Fidelity
                                    <sup>
                                        <CustomToolTip
                                            placement='top-start'
                                            title='Fidelity controls the granularity of schedule prediction, where low fidelity means quick processing and lesser details whereas high means higher processing and more details.'
                                            maxWidth='300px'
                                        >
                                            <InfoSvg />
                                        </CustomToolTip>
                                    </sup></span>
                                <div className='form__field__container' style={{ pointerEvents: 'none' }}>
                                    <Select id={1} placement="bottom" value={fidelity} style={{ width: '120px' }}
                                        onSelect={(value) => onChangePresimDetails(value as string)}

                                    >
                                        <Select.Trigger>
                                            <Select.Value placeholder="Select Operation" value={fidelity === "high" ? "High" : "Low"} />
                                        </Select.Trigger>
                                        <Select.Content>
                                            <Select.Group>
                                                <Select.Item value='high'>High</Select.Item>
                                                <Select.Item value='low'>Low</Select.Item>
                                            </Select.Group>
                                        </Select.Content>
                                    </Select>
                                </div>
                            </div>
                        }
                    </Fragment>
                }
                <div className='schedule__semulation__time__details__section'>
                    <div className='semulation__time__section'>
                        <span className='time__details__section__label'>Simulation Start Time</span>
                        <div className='time__input__container'>
                            <DatetimeInput
                                key={'simulationStartTime'}
                                value={simulationStartTime}
                                onChange={(value) => { onChangeSimulationTime(value) }}
                            />
                        </div>
                    </div>
                    <div className='semulation__duration__section'>
                        <span className='time__details__section__label'>Duration</span>
                        <div className='duration__input__container'>
                            <input type='number'
                                onKeyDown={validateNumberWithoutDecimal}
                                placeholder='Duration'
                                value={simulationDuration}
                                className='duration__input'
                                onChange={(event) => onChangeDuration(event.target.value)}
                            />
                            <div className='duration__parameter'>min</div>
                        </div>
                    </div>
                </div>
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}

export default ScenarioSchedule